import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastHost } from './components/Toast';
import { isApiError } from '@racksavant/core';
import { setAppNavigator } from './lib/api';
import { router } from './router';
import { setCacheReset, useAuthStore } from './store/auth';

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Never retry the errors that are the user's situation, not a blip.
          if (isApiError(error) && [400, 401, 403, 404, 422].includes(error.status)) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

export default function App() {
  const [queryClient] = useState(createQueryClient);
  const hydrate = useAuthStore((state) => state.hydrate);

  useEffect(() => {
    setAppNavigator((to) => void router.navigate(to));
    setCacheReset(() => queryClient.clear());
    void hydrate();
    return () => {
      setAppNavigator(null);
      setCacheReset(null);
    };
  }, [hydrate, queryClient]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ToastHost />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
